/*  ========================================================================
    # Components - Card
    ========================================================================  */

import React from 'react';
import PropTypes from 'prop-types';
import LazyImage from './LazyImage';
import useTranslation from '../hooks/useTranslation';
import useSVGSprite from '../hooks/useSVGSprite';

const CardTitle = props => {
  const { as: Tag = 'h4', children } = props;
  return <Tag className='c-card__title h4'>{children}</Tag>;
};

CardTitle.propTypes = {
  as: PropTypes.string,
  children: PropTypes.node
};

const Card = ({
  isSkeleton,
  image,
  title,
  label,
  buttonTranslationString,
  link,
  metaString,
  date,
  location,
  semanticHeadlineLevel
}) => {
  const __ = useTranslation();

  const handleClick = () => {
    if (link) {
      window.location.href = link;
    }
  };

  // Icons
  const sprite = useSVGSprite();

  return (
    <div className={`c-card ${isSkeleton ? 'c-card--skeleton' : ''}`}>
      <>
        {!isSkeleton ? (
          <>
            {image ? (
              <LazyImage
                defaultUrl={image.sizes['medium_large']}
                alt={image.alt}
                placeholderUrl={image.sizes['placeholder']}
                figureClass='c-card__image'
                onClick={handleClick}
              >
              </LazyImage>
            ) : (
              <div className='c-card__image c-card__image--empty'></div>
            )}
            <div className='c-card__content'>
              <div className='c-card__meta'>
                {label && (
                  <span
                    className='c-card__label'
                    style={{
                      '--label-color': label?.fontColor,
                      '--label-background': label?.backgroundColor
                    }}
                  >
                    {label?.name}
                  </span>
                )}
                {metaString && (
                  <span className='c-card__meta-string'>{__(metaString)}</span>
                )}
              </div>
              <CardTitle as={semanticHeadlineLevel}>{title}</CardTitle>
              <div className='c-card__append'>
                {date && <span className='c-card__date'>{date}</span>}
                {location && (
                  <span className='c-card__location'>
                    <svg viewBox='0 0 20 20' aria-hidden='true'>
                      <use
                        href={`${sprite}#${location === 'Online' ? 'monitor' : 'location'
                          }`}
                      >
                      </use>
                    </svg>
                    {location}
                  </span>
                )}
                <a
                  className='c-card__button link-with-chevron link-with-chevron--orange-dark'
                  href={link}
                  aria-label={buttonTranslationString}
                >
                  {__(buttonTranslationString)}
                </a>
              </div>
            </div>
          </>
        ) : null}

        {isSkeleton ? (
          <>
            <div className='c-card__image'></div>
            <div className='c-card__content'>
              <div className='c-card__meta'>
                <div className='c-card__label'></div>
                <div className='c-card__meta-string'></div>
              </div>
              <div className='c-card__title'></div>
              <div className='c-card__append'>
                <div className='c-card__date'></div>
                <div className='c-card__location'></div>
                <div className='c-card__button'></div>
              </div>
            </div>
          </>
        ) : null}
      </>
    </div>
  );
};

Card.propTypes = {
  isSkeleton: PropTypes.bool,
  image: PropTypes.object,
  title: PropTypes.string,
  label: PropTypes.object,
  buttonTranslationString: PropTypes.string,
  link: PropTypes.string,
  metaString: PropTypes.string,
  date: PropTypes.string,
  location: PropTypes.string,
  semanticHeadlineLevel: PropTypes.string
};

export default Card;
